import { Link } from 'react-router-dom';
import {
    Box,
    Collapse,
    List,
    ListItemButton,
    styled,
    SvgIcon,
    Typography,
} from '@mui/material';
import type { FunctionComponent } from 'react';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import ChevronRightIcon from '@heroicons/react/24/solid/ChevronRightIcon';
import type { NavEntryGroup, NavEntryItem, SidebarEntry } from './navTypes';
import { useSidebar } from './SidebarContext';

const pathInPathname = (path: string, pathname: string, exact = false) =>
    exact ? pathname === path : pathname.includes(path);

export type SideNavItemProps = NavEntryItem;

export const SidebarItem: FunctionComponent<SideNavItemProps> = ({
    disabled,
    icon,
    path,
    title,
    exact,
    external,
}) => {
    const { pathname } = useLocation();
    const { closeMobileSidebar } = useSidebar();
    // External entries open in a new tab and never match the current route, so
    // they are never highlighted as active.
    const active = !external && pathInPathname(path, pathname, exact);
    return (
        <ListItemButton
            sx={{ px: 2 }}
            component={external ? 'a' : Link}
            {...(external
                ? { href: path, target: '_blank', rel: 'noopener noreferrer' }
                : { to: path })}
            onClick={closeMobileSidebar}
        >
            <SidebarIcon active={active} fontSize='small'>
                {icon}
            </SidebarIcon>
            <SidebarLabel active={active} disabled={disabled ?? false}>
                {title}
            </SidebarLabel>
        </ListItemButton>
    );
};

export type SidebarGroupProps = NavEntryGroup;

export const SidebarGroup: FunctionComponent<SidebarGroupProps> = ({
    icon,
    title,
    entries,
}) => {
    const [open, setIsOpen] = useState(false);
    const { pathname } = useLocation();
    const { closeMobileSidebar } = useSidebar();

    const toggleGroup = () => setIsOpen((prev) => !prev);
    const hasActiveChild = entries.some(({ path, exact }) =>
        pathInPathname(path, pathname, exact)
    );
    const openCollapse = open || hasActiveChild;

    return (
        <>
            <ListItemButton sx={{ px: 2 }} onClick={toggleGroup}>
                <SidebarIcon active={hasActiveChild} fontSize='small'>
                    {icon}
                </SidebarIcon>
                <SidebarLabel active={hasActiveChild} disabled={false}>
                    {title}
                </SidebarLabel>
                <SidebarIcon
                    sx={{
                        fontSize: '1.1rem',
                        transform: `rotateZ(${openCollapse ? '90deg' : '0deg'})`,
                    }}
                    fontSize='small'
                    active={false}
                >
                    <ChevronRightIcon />
                </SidebarIcon>
            </ListItemButton>
            <Collapse in={openCollapse} timeout='auto' unmountOnExit>
                <List sx={{ ml: 6 }} component='div' disablePadding>
                    {entries.map(({ title, path, exact }, index) => {
                        const active = pathInPathname(path, pathname, exact);
                        return (
                            <ListItemButton
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    position: 'relative',
                                }}
                                key={index}
                                component={Link}
                                to={path}
                                onClick={closeMobileSidebar}
                            >
                                {active && (
                                    <Box
                                        sx={{
                                            mr: 3,
                                            backgroundColor: 'common.white',
                                            borderRadius: '50%',
                                            width: '6px',
                                            height: '6px',
                                            position: 'absolute',
                                            top: '50%',
                                            left: '8px',
                                            transform: 'translateY(-50%)',
                                        }}
                                    />
                                )}
                                <SidebarLabel
                                    sx={{ ml: 4 }}
                                    active={active}
                                    disabled={false}
                                >
                                    {title}
                                </SidebarLabel>
                            </ListItemButton>
                        );
                    })}
                </List>
            </Collapse>
        </>
    );
};

function sidebarMutedColor(theme: unknown): string {
    const palette = (
        theme as { palette?: { neutral?: Record<number, string> } }
    ).palette;
    return palette?.neutral?.[400] ?? '#9e9e9e';
}

const SidebarIcon = styled(SvgIcon, {
    shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ theme, active }) => ({
    alignItems: 'center',
    color: sidebarMutedColor(theme),
    display: 'inline-flex',
    justifyContent: 'center',
    marginRight: theme.spacing(2),
    ...(active && { color: '#fff' }),
}));

const SidebarLabel = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'active' && prop !== 'disabled',
})<{ active: boolean; disabled: boolean }>(({ theme, active, disabled }) => ({
    color: sidebarMutedColor(theme),
    flexGrow: 1,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '24px',
    whiteSpace: 'nowrap',
    ...(active && { color: theme.palette.common.white }),
    ...(disabled && { color: sidebarMutedColor(theme) }),
}));

export type { NavEntryGroup, NavEntryItem, SidebarEntry };
